import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Flame, Layers, Quote } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const GROUND_TENTS_HERO = "/products/tents/731077_481caff54c904ce1933b107b47be2aef~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ");

const WHY = [
  { icon: ShieldCheck, t: "Commercial-Grade Canvas", d: "Up to 320gsm Safari ripstop poly-canvas — the same grade trusted by African safari operators, built tougher than any weekend-camping tent." },
  { icon: Layers, t: "Heavy-Duty Floors", d: "Up to 500gsm PVC groundsheets — far tougher than the flimsy floors in most NZ-market tents." },
  { icon: Flame, t: "Proven In Harsh Conditions", d: "Trusted across Africa for remote overland travel and commercial safari operations for decades." },
];

// Already native elsewhere on the site (Drawers page) — cross-linked here, not duplicated
const PROMOTED_ACCESSORIES = [
  { name: "Clear Top Canvas Storage Bags", price: 74.95, tagline: "See Contents At A Glance", thumb: "/products/drawers/731077_456b25abb0b64e6cbdfa25bd0d756101~mv2.webp", to: "/drawers-kitchens-tables/clear-top-canvas-storage-bags" },
];

const Card = ({ m, testid }) => (
  <Link
    to={m.to || `/ground-tents/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {m.badge && (
        <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {m.badge}
          </span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-xl md:text-2xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <p className="font-display text-2xl text-[var(--brand-primary)] leading-none">
            {m.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
            ${fmt(m.price)}
          </p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function GroundTentsPage() {
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/groundtent`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  const BOW_TENTS = models.filter((m) => m.type === "bow-tent");
  const SAFARI_TENTS = models.filter((m) => m.type === "safari-tent");
  const ACCESSORIES = models.filter((m) => m.type === "accessory");

  return (
    <div data-testid="ground-tents-page">
      <SEO
        path="/ground-tents"
        title="Ground Tents NZ · Tentco Safari Canvas Tents"
        description="South African Tentco safari canvas ground tents — commercial-grade ripstop canvas, spring-steel frames. Bow tents, Sahara safari tents & extensions. From $249 to $3,995."
        image={GROUND_TENTS_HERO}
      />

      {/* HERO */}
      <section className="relative min-h-[42svh] md:min-h-[85svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={GROUND_TENTS_HERO} alt="Tentco safari canvas ground tent" className="w-full h-full object-cover" style={{ objectPosition: "50% 68%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-6% via-black/12 via-35% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-6 md:pb-20 pt-24 md:pt-32 w-full">
          <p className="overline mb-4 md:mb-6">— Tentco · South African Safari Canvas Tents</p>
          {/* H1 + intro + CTAs stay on the hero image on desktop only — they move below on mobile */}
          <div className="hidden md:block">
            <h1 className="font-display text-[9vw] lg:text-[7vw] leading-[0.85] text-white hero-text-shadow max-w-[16ch]">
              PROVEN IN AFRICA.<br />
              <span className="text-[var(--brand-primary)]">NOW RAISING</span><br />
              THE STANDARD.
            </h1>
            <div className="mt-10 grid grid-cols-2 gap-10 items-end">
              <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-xl">
                Built from genuine safari-grade ripstop poly-canvas (up to 320gsm) and heavy-duty PVC floors (up to 500gsm) — proper expedition-grade tents built much tougher than conventional NZ tents.
              </p>
              <div className="flex flex-wrap gap-3 justify-end">
                <a href="#bow-tents" data-testid="tents-cta-bowtents" className="btn-primary">Bow Tents <ArrowUpRight size={14} /></a>
                <a href="#safari-tents" data-testid="tents-cta-safaritents" className="btn-secondary">Sahara Safari Tents</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile only: H1 + CTAs sit below the hero image on the plain dark background, ahead of the supporting paragraph */}
      <section className="md:hidden bg-[var(--bg-main)] container-x pt-4 pb-6" data-testid="groundtents-hero-mobile-intro">
        <h1 className="font-display text-[10vw] leading-[0.85] text-white max-w-[14ch] mb-4">
          PROVEN IN AFRICA.<br />
          <span className="text-[var(--brand-primary)]">NOW RAISING</span><br />
          THE STANDARD.
        </h1>
        <div className="flex flex-wrap gap-3 mb-4">
          <a href="#bow-tents" data-testid="tents-cta-bowtents-mobile" className="btn-primary">Bow Tents <ArrowUpRight size={14} /></a>
          <a href="#safari-tents" data-testid="tents-cta-safaritents-mobile" className="btn-secondary">Sahara Safari Tents</a>
        </div>
        <p className="text-sm text-white/85 leading-relaxed">
          Built from genuine safari-grade ripstop poly-canvas (up to 320gsm) and heavy-duty PVC floors (up to 500gsm) — proper expedition-grade tents built much tougher than conventional NZ tents.
        </p>
      </section>

      {/* WHY TENTCO */}
      <section className="container-x py-14 md:py-20">
        <div className="mb-10">
          <p className="overline mb-3">— Ideal For</p>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95] max-w-3xl">
            4WD OVERLANDERS. HUNTERS. <span className="text-[var(--brand-primary)]">FAMILIES WHO WANT A TENT THAT LASTS.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} data-testid={`ground-tents-why-${i}`} className="bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={22} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
                <h3 className="font-display text-lg md:text-xl text-white mb-2">{w.t}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{w.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* BOW TENTS GRID */}
      <section id="bow-tents" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Bow Tents</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              BUILT FOR <span className="text-[var(--brand-primary)]">VEHICLE TOURING.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-4 max-w-2xl leading-relaxed">
              Two standalone steel-framed bow tents, each available in Junior (2.5m) and Senior (3.0m) sizes.
            </p>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BOW_TENTS.map((m, i) => <Card key={m.slug} m={m} testid={`bow-tent-${i}`} />)}
        </div>

        {/* TESTIMONIAL — proof point for the bow tents above (Wanderer, Safari Deluxe) */}
        <div data-testid="bow-tents-testimonial" className="mt-10 max-w-3xl border border-[var(--border-default)] bg-[var(--bg-secondary)] p-7 md:p-10">
          <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
            <Quote size={32} className="text-[var(--brand-primary)] shrink-0" strokeWidth={1.6} />
            <div>
              <p className="text-lg md:text-xl text-white leading-relaxed">
                "I had the same type of tent back in South Africa. Bought it in 2001, went all over Africa, sold it in
                2023 before we came over to NZ — the tent was still 100%. We used that tent a lot."
              </p>
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--brand-primary)] mt-5">
                — H.M. · 22 Years On The Same Tent
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SAFARI TENTS GRID */}
      <section id="safari-tents" className="container-x py-16 md:py-20 border-t border-[var(--border-default)] scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Sahara Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              COMMERCIAL-GRADE <span className="text-[var(--brand-primary)]">SAFARI TENTS.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-4 max-w-2xl leading-relaxed">
              Stainless steel-framed, fixed-size tents built for glamping operators, hunting camps, and families who want commercial-grade construction.
            </p>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SAFARI_TENTS.map((m, i) => <Card key={m.slug} m={m} testid={`safari-tent-${i}`} />)}
        </div>
      </section>

      {/* ACCESSORIES */}
      <section className="container-x pb-16 md:pb-20 border-t border-[var(--border-default)] pt-16 md:pt-20">
        <div className="mb-8">
          <p className="overline mb-3">— Extensions</p>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-[0.95]">
            EXTEND YOUR <span className="text-[var(--brand-primary)]">SETUP.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ACCESSORIES.map((m, i) => <Card key={m.slug} m={m} testid={`tent-accessory-${i}`} />)}
          {PROMOTED_ACCESSORIES.map((m, i) => <Card key={m.to} m={m} testid={`tent-accessory-promoted-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
